import { useId, useState } from 'react'

import { useBuscaDePerguntas } from '@/api/queries/compliance'

import { useAtrasado } from './useAtrasado'

export interface Requisito {
  padraoId: string
  perguntaId: string
  ano: number
  codigo: string
}

/**
 * "Requisitos atendidos" de `3c` — autocomplete por número ou texto da pergunta,
 * em TODOS os padrões (`GET /compliance/perguntas/busca`), com a seleção como
 * chips `ano · item` removíveis. Chips de padrões que não o selecionado ficam cinza.
 */
export function SeletorDeRequisitos({
  selecionados,
  aoMudar,
  padraoAtualId,
  desabilitado = false,
}: {
  selecionados: Requisito[]
  aoMudar: (proximos: Requisito[]) => void
  padraoAtualId: string | undefined
  desabilitado?: boolean
}) {
  const idLista = useId()
  const [termo, setTermo] = useState('')
  const atrasado = useAtrasado(termo)
  const busca = useBuscaDePerguntas(atrasado)
  const resultados = (busca.data ?? []).filter((r) => !selecionados.some((s) => s.perguntaId === r.perguntaId))

  return (
    <div className="flex flex-col gap-2">
      <div className="relative">
        <i className="ph ph-magnifying-glass text-icon-muted absolute top-1/2 left-3 -translate-y-1/2" aria-hidden="true" />
        <input
          type="search"
          role="combobox"
          aria-expanded={termo.trim() !== '' && resultados.length > 0}
          aria-controls={idLista}
          aria-label="Buscar requisito"
          placeholder="buscar por número ou texto da pergunta"
          value={termo}
          disabled={desabilitado}
          onChange={(e) => setTermo(e.target.value)}
          className="rounded-field border-border-subtle bg-surface-card text-text-primary type-body w-full border py-2 pr-3 pl-9"
        />
        {termo.trim() !== '' && (
          <ul
            id={idLista}
            role="listbox"
            aria-label="Requisitos encontrados"
            className="border-border-subtle bg-surface-card rounded-field shadow-pop absolute z-10 mt-1 max-h-64 w-full overflow-y-auto border"
          >
            {busca.isFetching && resultados.length === 0 && (
              <li className="text-text-muted px-3 py-2 type-sm">buscando…</li>
            )}
            {!busca.isFetching && atrasado === termo && resultados.length === 0 && (
              <li className="text-text-muted px-3 py-2 type-sm">nenhuma pergunta encontrada</li>
            )}
            {resultados.map((r) => (
              <li
                key={r.perguntaId}
                role="option"
                aria-selected={false}
                tabIndex={0}
                onClick={() => {
                  aoMudar([...selecionados, { padraoId: r.padraoId, perguntaId: r.perguntaId, ano: r.ano, codigo: r.codigo }])
                  setTermo('')
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    aoMudar([...selecionados, { padraoId: r.padraoId, perguntaId: r.perguntaId, ano: r.ano, codigo: r.codigo }])
                    setTermo('')
                  }
                }}
                className="hover:bg-subtle cursor-pointer px-3 py-2 type-sm"
              >
                <strong>
                  {r.ano} · {r.codigo}
                </strong>{' '}
                <span className="text-text-muted">{r.enunciado}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {selecionados.length > 0 && (
        <ul className="flex flex-wrap gap-2" aria-label="Requisitos selecionados">
          {selecionados.map((s) => (
            <li key={s.perguntaId}>
              <ChipDeRequisito requisito={s} atual={s.padraoId === padraoAtualId}>
                {!desabilitado && (
                  <button
                    type="button"
                    aria-label={`remover ${s.ano} · ${s.codigo}`}
                    onClick={() => aoMudar(selecionados.filter((x) => x.perguntaId !== s.perguntaId))}
                  >
                    <i className="ph ph-x" aria-hidden="true" />
                  </button>
                )}
              </ChipDeRequisito>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export function ChipDeRequisito({
  requisito,
  atual,
  children,
}: {
  requisito: { ano: number; codigo: string }
  atual: boolean
  children?: React.ReactNode
}) {
  return (
    <span
      className={`rounded-xs inline-flex items-center gap-1.5 px-2 py-1 type-sm font-semibold whitespace-nowrap ${
        atual ? 'bg-navy-50 text-iox-navy' : 'bg-gray-100 text-text-muted'
      }`}
    >
      {requisito.ano} · {requisito.codigo}
      {children}
    </span>
  )
}
